import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { NavigationMenu } from '../../components/NavigationMenu';
import { Button } from '../../components/Button';
import { LoadingAnimation } from '../../components/LoadingAnimation';
import { Brain, ArrowRight, Save, Instagram, Send } from 'lucide-react';
import { knowledgeApi, KnowledgeItem, contentPlanApi } from '../../services/api';
import { BackendContentPlanRequest } from '../../models/ContentPlan';
import { SaveContentDialog } from '../../components/SaveContentDialog';
import { useGeneration } from '../../context/GenerationContext';
import { useAuth } from '../../context/AuthContext';
import { useParams } from 'react-router-dom';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { KnowledgeSelector } from '../../components/KnowledgeSelector';
import { ContentPlanTable, ContentPlanData } from '../../components/ContentPlanTable';
import { localStorageService } from '../../services/localStorageService';
import { InterviewChat } from '../../components/InterviewChat';
import { InfoResearchChat } from '../../components/InfoResearchChat';
import { ContentPlanFormData } from '../../services/contentPlanInterviewService';
import { PageHeader } from '../../components/PageHeader';
import { useGenerationStatus } from '../../hooks/useGenerationStatus';
import { GenerationLimitsStatus } from '../../components/GenerationLimitsStatus';

// Функция для извлечения ника из ссылки
const extractUsernameFromLink = (input: string): string => {
  if (!input) return '';
  
  // Убираем пробелы
  let cleaned = input.trim();
  
  // Если это обычный ник без ссылки
  if (!/^https?:\/\//.test(cleaned) && !cleaned.includes('/')) {
    // Убираем @ если есть
    return cleaned.replace(/^@/, '');
  }
  
  try {
    // Создаем URL объект для парсинга
    const url = new URL(cleaned);
    
    // Извлекаем путь и убираем слеши
    let pathname = url.pathname.replace(/^\/+|\/+$/g, '');
    
    // Для Instagram и Telegram берем первую часть пути
    const parts = pathname.split('/');
    const username = parts[0];
    
    // Убираем @ если есть и возвращаем только буквы, цифры, точки и подчеркивания
    return username.replace(/^@/, '').replace(/[^a-zA-Z0-9_\.]/g, '');
  } catch (e) {
    // Если не удалось распарсить как URL, обрабатываем как простую строку
    return cleaned.replace(/^@/, '').replace(/[^a-zA-Z0-9_\.]/g, '');
  }
};

function ContentPlan() {
  const navigate = useCustomNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [contentPlanData, setContentPlanData] = useState<ContentPlanData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableContent, setEditableContent] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [articles, setArticles] = useState<KnowledgeItem[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const { startGeneration } = useGeneration();
  const { hasActiveSubscription } = useAuth();
  
  // Статус генерации для контент-плана
  const { status: generationStatus, loading: generationLoading, error: generationError } = useGenerationStatus('ContentPlan');
  
  // Состояние для интервью
  const [showInterview, setShowInterview] = useState(false);
  const [hasCompletedInterview, setHasCompletedInterview] = useState(false);
  const [showInfoResearch, setShowInfoResearch] = useState(false);
  
  // Состояние для уведомления о валидации
  const [validationMessage, setValidationMessage] = useState('');
  const [showValidationMessage, setShowValidationMessage] = useState(false);
  
  useDocumentTitle('Контент-план');
  
  const [formData, setFormData] = useState({
    topic: '',
    description: '',
    services: '',
    aboutAuthor: '',
    contentPreferences: '', // предпочтения в контенте (локации, типы съемки и т.д.)
    audienceArticleId: null as string | null,
    authorSocialHandle: '', // основной аккаунт автора
    competitorSocialHandle: '', // аккаунт конкурента (необязательно) 
    enableDeepResearch: true, // по умолчанию включено

    socialNetwork: 'instagram' as 'instagram' | 'telegram',
    instagramFormats: {
      reels: true,
      carousel: true,
      post: true
    },
    reelsComplexity: 'medium' as 'simple' | 'medium' | 'complex',
    frequency: {
      reels: 2,
      carousel: 2,
      post: 3,
      telegram: 3
    },
    duration: 7, // дней по умолчанию (1 неделя)
    schedule: {
      postsPerWeek: 3,
      reelsPerWeek: 2,
      bestDays: ['mon', 'wed', 'fri'],
      bestTime: '10:00'
    }
  });

  // Состояния для управления вводом аккаунтов
  const [isEditingSocialHandle, setIsEditingSocialHandle] = useState(true);
  const [isEditingCompetitorHandle, setIsEditingCompetitorHandle] = useState(true);
  const [showSocialHandleError, setShowSocialHandleError] = useState(false);
  const [showCompetitorHandleError, setShowCompetitorHandleError] = useState(false);
  const [socialHandleErrorTimeout, setSocialHandleErrorTimeout] = useState<NodeJS.Timeout | null>(null);
  const [competitorHandleErrorTimeout, setCompetitorHandleErrorTimeout] = useState<NodeJS.Timeout | null>(null);
  const socialHandleInputRef = useRef<HTMLInputElement>(null);
  const competitorHandleInputRef = useRef<HTMLInputElement>(null);

  // Константные значения для типов файлов, чтобы избежать перерендеров
  const audienceFileTypes = useMemo(() => ['audience'], []);
  const emptyFileTypes = useMemo(() => [], []);

  // Состояния для выбранных документов (для отображения имен)
  const [selectedAudienceDoc, setSelectedAudienceDoc] = useState<KnowledgeItem | null>(null);

  // Функция для поиска документа по ID
  const findDocumentById = useCallback((documentId: string | null): KnowledgeItem | null => {
    if (!documentId || !articles.length) return null;
    
    const findInItems = (items: KnowledgeItem[]): KnowledgeItem | null => {
      for (const item of items) {
        if (item.id === documentId) return item;
        if (item.children) {
          const found = findInItems(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findInItems(articles);
  }, [articles]);

  // Обновление выбранных документов при изменении formData или загрузке articles
  useEffect(() => {
    const audienceDoc = findDocumentById(formData.audienceArticleId);
    
    console.log('[ContentPlan] Updating selected documents:', {
      audienceArticleId: formData.audienceArticleId,
      audienceDoc: audienceDoc?.name,
      articlesLength: articles.length
    });
    
    setSelectedAudienceDoc(audienceDoc);
  }, [formData.audienceArticleId, findDocumentById]);

  // Загрузка черновика при монтировании компонента
  useEffect(() => {
    const savedDraft = localStorageService.getContentPlanDraft<typeof formData>();
    if (savedDraft) {
      // Используем проверки на наличие данных для предотвращения установки пустых значений
      const newFormData = { ...formData };
      let hasActualData = false;
      
      // Обновляем только те поля, которые есть в черновике
      if (savedDraft.topic) {
        newFormData.topic = savedDraft.topic;
        hasActualData = true;
      }
      if (savedDraft.description) {
        newFormData.description = savedDraft.description;
        hasActualData = true;
      }
      if (savedDraft.services) {
        newFormData.services = savedDraft.services;
        hasActualData = true;
      }
      if (savedDraft.aboutAuthor) {
        newFormData.aboutAuthor = savedDraft.aboutAuthor;
        hasActualData = true;
      }
      if (savedDraft.contentPreferences) {
        newFormData.contentPreferences = savedDraft.contentPreferences;
        hasActualData = true;
      }
      if (savedDraft.audienceArticleId !== undefined && savedDraft.audienceArticleId !== null) {
        newFormData.audienceArticleId = savedDraft.audienceArticleId;
        console.log('[ContentPlan] Restored audienceArticleId from draft:', savedDraft.audienceArticleId);
      }
      if (savedDraft.authorSocialHandle) newFormData.authorSocialHandle = savedDraft.authorSocialHandle;
      if (savedDraft.competitorSocialHandle) newFormData.competitorSocialHandle = savedDraft.competitorSocialHandle;
      if (savedDraft.enableDeepResearch !== undefined) newFormData.enableDeepResearch = savedDraft.enableDeepResearch;
      if (savedDraft.socialNetwork) newFormData.socialNetwork = savedDraft.socialNetwork;
      if (savedDraft.instagramFormats) newFormData.instagramFormats = savedDraft.instagramFormats;
      if (savedDraft.reelsComplexity) newFormData.reelsComplexity = savedDraft.reelsComplexity;
      if (savedDraft.frequency) newFormData.frequency = savedDraft.frequency;
      if (savedDraft.duration) newFormData.duration = savedDraft.duration;
      if (savedDraft.schedule) newFormData.schedule = savedDraft.schedule;
      
      // Устанавливаем обновленное состояние за один раз
      setFormData(newFormData);
      
      // Автоматически изменяем размер всех textarea после загрузки черновика
      autoResizeAllTextareas();
      
      // Проверка наличия значений для аккаунтов
      if (savedDraft.authorSocialHandle) {
        setIsEditingSocialHandle(false);
      }
      if (savedDraft.competitorSocialHandle) {
        setIsEditingCompetitorHandle(false);
      }
      
      // Если есть черновик с реальными данными, считаем что интервью завершено
      if (hasActualData) {
        setHasCompletedInterview(true);
      }
    } else {
      // Если нет черновика, очищаем состояние интервью
      setHasCompletedInterview(false);
    }
  }, []);

  // Сохранение черновика при изменении formData
  useEffect(() => {
    // Используем debounce для сохранения черновика, чтобы не перегружать localStorage
    const saveTimeout = setTimeout(() => {
      console.log('[ContentPlan] Saving draft with formData:', {
        audienceArticleId: formData.audienceArticleId,
        topic: formData.topic
      });
      localStorageService.saveContentPlanDraft(formData);
    }, 500); // задержка в 500 мс для дебаунсинга
    
    return () => {
      clearTimeout(saveTimeout);
    };
  }, [formData]);

  // Функция для очистки черновика после успешного создания контент-плана
  const clearContentPlanDraft = () => {
    localStorageService.clearContentPlanDraft();
  };

  // Функция для автоматического изменения высоты textarea
  const autoResizeTextarea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Функция для автоматического изменения размера всех textarea на странице
  const autoResizeAllTextareas = useCallback(() => {
    // Используем setTimeout чтобы дать React время обновить DOM
    setTimeout(() => {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach(textarea => {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      });
    }, 50);
  }, []);

  // Обработчик завершения интервью
  const handleInterviewCompleted = (extractedData: ContentPlanFormData) => {
    // Заполняем поля формы данными из интервью
    setFormData(prev => ({
      ...prev,
      topic: extractedData.topic || prev.topic,
      description: extractedData.description || prev.description,
      services: extractedData.services || prev.services,
      aboutAuthor: extractedData.aboutAuthor || prev.aboutAuthor,
      contentPreferences: extractedData.contentPreferences || prev.contentPreferences
    }));
    
    // Автоматически изменяем размер всех textarea после заполнения
    autoResizeAllTextareas();
    
    // Закрываем интервью и помечаем как завершенное
    setShowInterview(false);
    setHasCompletedInterview(true);
    
    // Показываем сообщение об успехе
  };

  useEffect(() => {
    const loadItems = async () => {
      try {
        console.log('[ContentPlan] Loading articles...');
        
        // Сначала пытаемся загрузить из кэша
        const cachedItems = localStorageService.getKnowledgeCache();
        if (cachedItems) {
          console.log('[ContentPlan] Using cached articles:', cachedItems.length, 'items');
          setArticles(cachedItems);
          return;
        }
        
        // Если кэша нет, загружаем с сервера
        console.log('[ContentPlan] Loading articles from server...');
        const items = await knowledgeApi.getItems();
        console.log('[ContentPlan] Articles loaded from server:', items.length, 'items');
        
        // Сохраняем в кэш
        localStorageService.saveKnowledgeCache(items);
        setArticles(items);
      } catch (error) {
        console.error('Error loading articles:', error);
      }
    };
    loadItems();
  }, []);

  const handleAudienceArticleSelect = (item: KnowledgeItem | null) => {
    console.log('[ContentPlan] Selected audience article:', item?.id, item?.name);
    setFormData(prev => ({
      ...prev,
      audienceArticleId: item?.id || null
    }));
  };
  

  // Функция валидации обязательных полей
  const validateRequiredFields = () => {
    const requiredFields = [];
    
    if (!formData.topic.trim()) {
      requiredFields.push('Тематика блога');
    }
    
    if (requiredFields.length > 0) {
      setValidationMessage(`Необходимо заполнить: ${requiredFields.join(', ')}`);
      setShowValidationMessage(true);
      
      // Скрываем сообщение через 5 секунд
      setTimeout(() => {
        setShowValidationMessage(false);
      }, 5000);
      
      return false;
    }
    
    return true;
  };

  const handleGenerateContentPlan = async () => {
    // Проверяем наличие активной подписки
    if (!hasActiveSubscription) {
      navigate('/tariffs');
      return;
    }

    // Проверяем возможность генерации
    if (generationStatus && !generationStatus.canGenerate) {
      return;
    }

    // Валидируем обязательные поля
    if (!validateRequiredFields()) {
      return;
    }

    setIsLoading(true);
    setContentPlanData(null);
    
    try {
      const descriptionWords = formData.description.split(/[\s.,;!?]+/).filter(word => word.length > 3);
      const potentialKeywords = [...new Set(descriptionWords)].slice(0, 5);
      
      const requestData: BackendContentPlanRequest = {
        topic: formData.topic,
        description: formData.description,
        services: formData.services,
        aboutAuthor: formData.aboutAuthor,
        contentPreferences: formData.contentPreferences,
        audienceArticleId: formData.audienceArticleId,
        authorSocialHandle: formData.authorSocialHandle,
        referenceSocialHandle: formData.competitorSocialHandle,
        enableDeepResearch: formData.enableDeepResearch,
        socialNetworks: {
          instagram: formData.socialNetwork === 'instagram',
          telegram: formData.socialNetwork === 'telegram'
        },
        instagramFormats: formData.instagramFormats,
        reelsComplexity: formData.reelsComplexity,
        frequency: formData.frequency,
        duration: formData.duration,
        keywords: potentialKeywords,
        goals: [
          `Повышение узнаваемости в теме ${formData.topic}`,
          'Увеличение вовлеченности аудитории',
          'Рост числа подписчиков'
        ],
        selectedArticleIds: []
      };
      
      console.log('API request:', requestData);
      
      // Отправляем запрос на сервер (не ждем ответа)
      contentPlanApi.generate(requestData).then((response) => {
        console.log('API response:', response.data);
        
        // Задача будет автоматически добавлена через SignalR уведомления от бэкенда
        // startGeneration убран, чтобы избежать дублирования
        if (response.data && response.data.taskId) {
          console.log(`[Debug] Получен taskId: ${response.data.taskId}`);
        }
      }).catch((error) => {
        console.error('Error generating content plan:', error);
        // Ошибки обрабатываются в фоне, не влияют на UI
      });
      
      // Очищаем черновик после запуска генерации
      clearContentPlanDraft();
      
      // Показываем анимацию 4 секунды, затем переходим на страницу задач
      setTimeout(() => {
        setIsLoading(false);
        navigate('/tasks');
      }, 4000);
      
    } catch (error) {
      console.error('Error starting content plan generation:', error);
      setIsLoading(false);
    }
  };

  const handleSave = async (targetFolderId?: string | null, fileName?: string) => {
    if (!contentPlanData) {
        return;
    }
    
    if (targetFolderId !== undefined && fileName) {
      try {
        const contentPlanJson = JSON.stringify(contentPlanData);
        
        const newItem: KnowledgeItem = {
          id: 'temp-' + Date.now(),
          itemType: 'file',
          fileType: 'content-plan',
          name: fileName,
          content: contentPlanJson,
          parentId: targetFolderId,
        };

        await knowledgeApi.saveItem(newItem);
        navigate('/knowledge');
        setShowSaveDialog(false);
      } catch (error) {
        console.error('Failed to save content plan:', error);
      }
    } else if (!fileName) {
      const blob = new Blob([JSON.stringify(contentPlanData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formData.topic || 'content-plan'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const toggleEditMode = () => {
    if (!isEditing) {
      setEditableContent(contentPlanData ? JSON.stringify(contentPlanData) : '');
    } else {
      setContentPlanData(JSON.parse(editableContent));
    }
    setIsEditing(!isEditing);
  };

  const openSaveDialog = () => {
    setShowSaveDialog(true);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    
    // Автоматическое изменение высоты для textarea
    if (e.target instanceof HTMLTextAreaElement) {
      autoResizeTextarea(e as React.ChangeEvent<HTMLTextAreaElement>);
    }
    
    // Для полей социальных сетей фильтруем русские буквы и недопустимые символы
    if (name === 'authorSocialHandle' || name === 'competitorSocialHandle') {
      // Если это URL Instagram, не фильтруем
      if (value.includes('instagram.com')) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      } else {
        // Фильтруем только латинские буквы, цифры, точки, подчеркивания и @
        const filteredValue = value.replace(/[^a-zA-Z0-9._@]/g, '');
        setFormData(prev => ({
          ...prev,
          [name]: filteredValue
        }));
      }
    } else {
      // Для остальных полей сохраняем значение как есть
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Отдельный обработчик для потери фокуса полей социальных сетей
  const handleSocialHandleBlur = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    
    if (name === 'authorSocialHandle' || name === 'competitorSocialHandle') {
      // Извлекаем ник из ссылки или очищаем от недопустимых символов
      const extractedUsername = extractUsernameFromLink(value);
      
      // Проверяем, было ли что-то изменено (ссылка преобразована или символы отфильтрованы)
      if (value !== extractedUsername && value.trim() !== '') {
        const isAuthorField = name === 'authorSocialHandle';
        const currentTimeout = isAuthorField ? socialHandleErrorTimeout : competitorHandleErrorTimeout;
        const setShowError = isAuthorField ? setShowSocialHandleError : setShowCompetitorHandleError;
        const setErrorTimeout = isAuthorField ? setSocialHandleErrorTimeout : setCompetitorHandleErrorTimeout;
        
        // Отменяем предыдущий таймер, если он есть
        if (currentTimeout) {
          clearTimeout(currentTimeout);
        }
        
        // Показываем сообщение об ошибке/преобразовании
        setShowError(true);
        
        // Устанавливаем таймер для скрытия сообщения
        const timeoutId = setTimeout(() => {
          setShowError(false);
        }, 3000);
        
        setErrorTimeout(timeoutId);
      }
      
      // Устанавливаем извлеченный/очищенный ник
      setFormData(prev => ({
        ...prev,
        [name]: extractedUsername
      }));
    }
  };
  
  const handleSocialNetworkChange = (network: 'instagram' | 'telegram') => {
    // Блокируем выбор Telegram
    if (network === 'telegram') {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      socialNetwork: network
    }));
  };
  
  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = e.target;
    
    if (name.startsWith('instagramFormats.')) {
      const format = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        instagramFormats: {
          ...prev.instagramFormats,
          [format]: checked
        }
      }));
    }
  };
  
  const handleFrequencyChange = (type: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      frequency: {
        ...prev.frequency,
        [type]: value
      }
    }));
  };




  
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-0">
                Основная информация
              </h2>
              {/* Показываем кнопку ИИ только если поля уже видны */}
              {(hasCompletedInterview || formData.topic || formData.description || formData.services || formData.aboutAuthor || formData.contentPreferences) && (
                <Button
                  onClick={() => setShowInterview(true)}
                  variant="primary"
                  className="flex items-center justify-center bg-gradient-to-r from-purple-500 to-indigo-600 !text-white transition-all duration-200 shadow-md transform border-none"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  Интервью с ИИ
                </Button>
              )}
            </div>
            
            {/* Показываем поля только если есть данные или интервью завершено */}
            {(hasCompletedInterview || formData.topic || formData.description || formData.services || formData.aboutAuthor || formData.contentPreferences) ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Тематика блога
                  </label>
                  <div className="mb-1">
                    <p className="text-xs italic text-gray-600 dark:text-gray-400">
                      Укажите конкретную нишу вашего блога. Чем точнее тема, тем качественнее будет контент-план.
                    </p>
                  </div>
                  <textarea
                    name="topic"
                    value={formData.topic}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white"
                    placeholder="Например: Маркетинг для малого бизнеса"
                    rows={1}
                    style={{minHeight: '40px', overflow: 'hidden'}}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Описание цели блога
                  </label>
                  <div className="mb-1">
                    <p className="text-xs italic text-gray-600 dark:text-gray-400">
                      Опишите бизнес-цели, которые планируете достичь с помощью контента (привлечение клиентов, узнаваемость бренда и т.д.)
                    </p>
                  </div>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white"
                    placeholder="Опишите цели вашего контент-плана..."
                    rows={1}
                    style={{minHeight: '40px', overflow: 'hidden'}}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Опишите ваши услуги, продукт, если есть
                  </label>
                  <div className="mb-1">
                    <p className="text-xs italic text-gray-600 dark:text-gray-400">
                      Перечислите продукты или услуги, которые хотите продвигать через контент и их ключевые преимущества.
                    </p>
                  </div>
                  <textarea
                    name="services"
                    value={formData.services}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white"
                    placeholder="Расскажите о ваших услугах или продуктах..."
                    rows={1}
                    style={{minHeight: '40px', overflow: 'hidden'}}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Опишите себя
                  </label>
                  <div className="mb-1">
                    <p className="text-xs italic text-gray-600 dark:text-gray-400">
                      Расскажите о вашей экспертизе, опыте и ценностях бренда. Это поможет создать аутентичный контент.
                    </p>
                  </div>
                  <textarea
                    name="aboutAuthor"
                    value={formData.aboutAuthor}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white"
                    placeholder="Расскажите немного о себе или вашем бренде..."
                    rows={1}
                    style={{minHeight: '40px', overflow: 'hidden'}}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Предпочтения в контенте
                  </label>
                  <div className="mb-1">
                    <p className="text-xs italic text-gray-600 dark:text-gray-400">
                      Укажите ваши предпочтения по локациям съемки, типам контента, стилю подачи и другим особенностям, которые важны для вашего бренда.
                    </p>
                  </div>
                  <textarea
                    name="contentPreferences"
                    value={formData.contentPreferences}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white"
                    placeholder="Например: предпочитаю съемку в студии, минималистичный стиль, избегаю слишком яркие цвета..."
                    rows={1}
                    style={{minHeight: '40px', overflow: 'hidden'}}
                  />
                </div>
                
              </div>
            ) : (
              // Показываем кнопку интервью если основные поля пустые
              <div className="space-y-6">
                <div className="text-center py-12">
                  <div className="max-w-md mx-auto">
                    <div className="mb-6">
                      <Brain className="w-16 h-16 mx-auto text-blue-500 dark:text-blue-400 mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        Начните с интервью
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        ИИ поможет заполнить все необходимые поля для создания контент-плана
                      </p>
                    </div>
                    <div className="flex flex-col items-center space-y-3">
                      <Button
                        onClick={() => setShowInterview(true)}
                        variant="primary"
                        className="flex items-center justify-center bg-gradient-to-r from-purple-500 to-indigo-600 !text-white transition-all duration-200 shadow-md transform border-none"
                      >
                        <Brain className="w-4 h-4 mr-2" />
                        Начать интервью
                      </Button>
                      <button
                        onClick={() => setHasCompletedInterview(true)}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                      >
                        Показать поля
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Поля выбора документов показываем всегда, независимо от состояния */}
            <div className="mt-8 space-y-6 hidden">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Целевая аудитория
                </label>
                <div className="mb-1">
                  <p className="text-xs italic text-gray-600 dark:text-gray-400">
                    Выберите документ с анализом целевой аудитории для адаптации контента под интересы подписчиков.
                  </p>
                </div>
                <div className=""> 
                  <KnowledgeSelector
                    onSelect={handleAudienceArticleSelect}
                    placeholder="Выберите документ с целевой аудиторией"
                    fileTypes={audienceFileTypes}
                    selectedItemId={formData.audienceArticleId}
                    preloadedItems={articles}
                  />
                </div>
              </div>
              
            </div>
            
            {/* Кнопка "Далее" и статус лимитов показываем всегда внизу */}
            <div className="mt-8">
              <GenerationLimitsStatus 
                className="mb-4" 
                generationType="ContentPlan" 
                status={generationStatus}
                loading={generationLoading}
                error={generationError}
              />
              <div className="flex justify-end">
                <Button
                  onClick={() => setCurrentStep(2)}
                  variant="primary"
                  className="flex items-center"
                >
                  Далее
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
              Настройки контент-плана
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">
                  Выберите социальную сеть
                </h3>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <Button
                    onClick={() => handleSocialNetworkChange('instagram')}
                    variant="primary"
                    className={`flex flex-col items-center justify-center p-4 transition-all ${
                      formData.socialNetwork === 'instagram' 
                        ? 'bg-blue-100 dark:bg-blue-900/40 border-2 border-blue-500 dark:border-blue-500' 
                        : 'bg-gray-100 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-blue-600/20'
                    }`}
                  >
                    <div className={`p-3 rounded-full mb-2 ${
                      formData.socialNetwork === 'instagram'
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      <Instagram className="w-6 h-6" />
                    </div>
                    <span className={`font-medium ${
                      formData.socialNetwork === 'instagram'
                        ? 'text-blue-700 dark:text-blue-400' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      Instagram
                    </span>
                  </Button>
                  
                  <Button
                    onClick={() => handleSocialNetworkChange('telegram')}
                    variant="primary"
                    disabled={true}
                    className="flex flex-col items-center justify-center p-4 transition-all opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600"
                  >
                    <div className="p-3 rounded-full mb-2 bg-gray-200 dark:bg-gray-700 text-gray-400">
                      <Send className="w-6 h-6" />
                    </div>
                    <span className="font-medium text-gray-400">
                      Telegram (скоро)
                    </span>
                  </Button>
                </div>

                {/* Поля для ввода двух аккаунтов Instagram */}
                <div className="mt-4 space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6">
                  {/* Основной аккаунт */}
                  <div className="relative">
                    <label htmlFor="authorSocialHandle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Ваш аккаунт Instagram
                    </label>
                    
                    {showSocialHandleError && (
                      <div className="absolute -top-14 sm:-top-10 left-0 right-0 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 p-2 rounded-md text-xs border border-blue-200 dark:border-blue-800 shadow-sm z-10 text-center">
                        Ссылка преобразована в ник. Можно вставлять ссылки или вводить ник напрямую
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      {isEditingSocialHandle ? (
                        <input
                          type="text"
                          name="authorSocialHandle"
                          id="authorSocialHandle"
                          value={formData.authorSocialHandle}
                          onChange={handleInputChange}
                          onBlur={(e) => {
                            handleSocialHandleBlur(e);
                            // Проверяем актуальное значение после преобразования
                            const extractedUsername = extractUsernameFromLink(e.target.value);
                            if (extractedUsername.trim()) {
                              setIsEditingSocialHandle(false);
                            }
                          }}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white"
                          placeholder="ник или ссылка: instagram.com/ваш_профиль"
                          ref={socialHandleInputRef}
                        />
                      ) : formData.authorSocialHandle ? (
                        <div 
                          onClick={() => setIsEditingSocialHandle(true)}
                          className="w-full inline-flex items-center px-3 py-2 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md text-sm cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-colors"
                        >
                          <Instagram className="w-4 h-4 mr-2" />
                          @{formData.authorSocialHandle}
                        </div>
                      ) : (
                        <button
                          onClick={() => setIsEditingSocialHandle(true)}
                          className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center justify-center"
                        >
                          <Instagram className="w-4 h-4 mr-2" />
                          Добавить профиль Instagram
                        </button>
                      )}
                    </div>
                    
                    <div className="mt-2 bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3 flex items-start">
                      <div className="mr-2 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="20" x2="18" y2="10"></line>
                          <line x1="12" y1="20" x2="12" y2="4"></line>
                          <line x1="6" y1="20" x2="6" y2="14"></line>
                          <line x1="3" y1="20" x2="21" y2="20"></line>
                        </svg>
                      </div>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <span className="font-medium">Анализ вашего профиля:</span> изучим стиль подачи контента и тематику для создания адаптированного под ваш бренд контент-плана.
                      </p>
                    </div>
                  </div>

                  {/* Аккаунт конкурента */}
                  <div className="relative">
                    <label htmlFor="competitorSocialHandle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Референс-аккаунт
                    </label>
                    
                    {showCompetitorHandleError && (
                      <div className="absolute -top-14 sm:-top-10 left-0 right-0 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 p-2 rounded-md text-xs border border-blue-200 dark:border-blue-800 shadow-sm z-10 text-center">
                        Ссылка преобразована в ник. Можно вставлять ссылки или вводить ник напрямую
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      {isEditingCompetitorHandle ? (
                        <input
                          type="text"
                          name="competitorSocialHandle"
                          id="competitorSocialHandle"
                          value={formData.competitorSocialHandle}
                          onChange={handleInputChange}
                          onBlur={(e) => {
                            handleSocialHandleBlur(e);
                            // Проверяем актуальное значение после преобразования
                            const extractedUsername = extractUsernameFromLink(e.target.value);
                            if (extractedUsername.trim()) {
                              setIsEditingCompetitorHandle(false);
                            }
                          }}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white"
                          placeholder="ник или ссылка: instagram.com/другой_аккаунт"
                          ref={competitorHandleInputRef}
                        />
                      ) : formData.competitorSocialHandle ? (
                        <div 
                          onClick={() => setIsEditingCompetitorHandle(true)}
                          className="w-full inline-flex items-center px-3 py-2 bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded-md text-sm cursor-pointer hover:bg-orange-200 dark:hover:bg-orange-800/40 transition-colors"
                        >
                          <Instagram className="w-4 h-4 mr-2" />
                          @{formData.competitorSocialHandle}
                        </div>
                      ) : (
                        <button
                          onClick={() => setIsEditingCompetitorHandle(true)}
                          className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 hover:border-orange-400 hover:text-orange-600 dark:hover:text-orange-400 transition-colors flex items-center justify-center"
                        >
                          <Instagram className="w-4 h-4 mr-2" />
                          Добавить аккаунт
                        </button>
                      )}
                    </div>
                    
                    <div className="mt-2 bg-orange-50 dark:bg-orange-900/10 rounded-lg p-3 flex items-start">
                      <div className="mr-2 text-orange-500 dark:text-orange-400 flex-shrink-0 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3"></circle>
                          <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
                        </svg>
                      </div>
                      <p className="text-xs text-orange-700 dark:text-orange-300">
                        <span className="font-medium">Анализ референс-аккаунта:</span> изучим стратегию и стиль подачи контента для выявления эффективных подходов и используем при создании контент-плана.
                      </p>
                    </div>
                  </div>
                </div>

                  {/* Кнопка для исследования инфоповодов */}
                  <div className="mt-4">
                    <Button
                      onClick={() => setShowInfoResearch(true)}
                      variant="primary"
                      className="flex items-center"
                    >
                      Поиск инфоповодов (скоро)
                    </Button>
                  </div>
                </div>
              
              {formData.socialNetwork === 'instagram' && (
                <div>
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">
                    Форматы для Instagram
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div 
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.instagramFormats.reels 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500 dark:text-gray-300' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:text-gray-300'
                      }`}
                      onClick={() => {
                        const newValue = !formData.instagramFormats.reels;
                        const event = {
                          target: {
                            name: 'instagramFormats.reels',
                            checked: newValue
                          }
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleCheckboxChange(event);
                      }}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                        formData.instagramFormats.reels
                          ? 'bg-blue-500' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}>
                        <input 
                          type="checkbox" 
                          name="instagramFormats.reels"
                          checked={formData.instagramFormats.reels}
                          onChange={handleCheckboxChange}
                          className="opacity-0 absolute"
                          id="reels-checkbox"
                        />
                        {formData.instagramFormats.reels && (
                          <svg className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <label htmlFor="reels-checkbox" className="font-medium cursor-pointer select-none">Reels</label>
                    </div>

                    <div 
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.instagramFormats.carousel 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500 dark:text-gray-300' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:text-gray-300'
                      }`}
                      onClick={() => {
                        const newValue = !formData.instagramFormats.carousel;
                        const event = {
                          target: {
                            name: 'instagramFormats.carousel',
                            checked: newValue
                          }
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleCheckboxChange(event);
                      }}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                        formData.instagramFormats.carousel
                          ? 'bg-blue-500' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}>
                        <input 
                          type="checkbox" 
                          name="instagramFormats.carousel"
                          checked={formData.instagramFormats.carousel}
                          onChange={handleCheckboxChange}
                          className="opacity-0 absolute"
                          id="carousel-checkbox"
                        />
                        {formData.instagramFormats.carousel && (
                          <svg className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <label htmlFor="carousel-checkbox" className="font-medium cursor-pointer select-none">Карусель</label>
                    </div>

                    <div 
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.instagramFormats.post 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500 dark:text-gray-300' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:text-gray-300'
                      }`}
                      onClick={() => {
                        const newValue = !formData.instagramFormats.post;
                        const event = {
                          target: {
                            name: 'instagramFormats.post',
                            checked: newValue
                          }
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleCheckboxChange(event);
                      }}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                        formData.instagramFormats.post
                          ? 'bg-blue-500' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}>
                        <input 
                          type="checkbox" 
                          name="instagramFormats.post"
                          checked={formData.instagramFormats.post}
                          onChange={handleCheckboxChange}
                          className="opacity-0 absolute"
                          id="post-checkbox"
                        />
                        {formData.instagramFormats.post && (
                          <svg className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <label htmlFor="post-checkbox" className="font-medium cursor-pointer select-none">Посты</label>
                    </div>
                  </div>
                </div>
              )}
              
              {formData.socialNetwork === 'instagram' && formData.instagramFormats.reels && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">
                    Уровень сложности сценариев Reels
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div 
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.reelsComplexity === 'simple' 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      onClick={() => setFormData(prev => ({ ...prev, reelsComplexity: 'simple' }))}
                    >
                      <span className={`font-medium text-center ${
                        formData.reelsComplexity === 'simple'
                          ? 'text-blue-700 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>Простой</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Базовые сценарии, легкие в реализации
                      </p>
                    </div>
                    
                    <div 
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.reelsComplexity === 'medium' 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      onClick={() => setFormData(prev => ({ ...prev, reelsComplexity: 'medium' }))}
                    >
                      <span className={`font-medium text-center ${
                        formData.reelsComplexity === 'medium'
                          ? 'text-blue-700 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>Средний</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Сбалансированные сценарии средней сложности
                      </p>
                    </div>
                    
                    <div 
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.reelsComplexity === 'complex' 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      onClick={() => setFormData(prev => ({ ...prev, reelsComplexity: 'complex' }))}
                    >
                      <span className={`font-medium text-center ${
                        formData.reelsComplexity === 'complex'
                          ? 'text-blue-700 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>Сложный</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Креативные сценарии с продвинутыми элементами
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">
                  Частота публикаций (в неделю)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {formData.socialNetwork === 'instagram' && formData.instagramFormats.reels && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Количество Reels
                      </label>
                      <div className="flex items-center justify-center">
                        <button 
                          onClick={() => handleFrequencyChange('reels', Math.max(0, formData.frequency.reels - 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-l-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <input
                          type="number"
                          min="0"
                          max="7"
                          value={formData.frequency.reels}
                          onChange={(e) => handleFrequencyChange('reels', parseInt(e.target.value) || 0)}
                          className="w-16 h-10 text-center border-t border-b border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg font-medium"
                        />
                        <button 
                          onClick={() => handleFrequencyChange('reels', Math.min(7, formData.frequency.reels + 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-r-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {formData.socialNetwork === 'instagram' && formData.instagramFormats.carousel && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Количество каруселей
                      </label>
                      <div className="flex items-center justify-center">
                        <button 
                          onClick={() => handleFrequencyChange('carousel', Math.max(0, formData.frequency.carousel - 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-l-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <input
                          type="number"
                          min="0"
                          max="7"
                          value={formData.frequency.carousel}
                          onChange={(e) => handleFrequencyChange('carousel', parseInt(e.target.value) || 0)}
                          className="w-16 h-10 text-center border-t border-b border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg font-medium"
                        />
                        <button 
                          onClick={() => handleFrequencyChange('carousel', Math.min(7, formData.frequency.carousel + 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-r-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {formData.socialNetwork === 'instagram' && formData.instagramFormats.post && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Количество постов
                      </label>
                      <div className="flex items-center justify-center">
                        <button 
                          onClick={() => handleFrequencyChange('post', Math.max(0, formData.frequency.post - 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-l-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <input
                          type="number"
                          min="0"
                          max="7"
                          value={formData.frequency.post}
                          onChange={(e) => handleFrequencyChange('post', parseInt(e.target.value) || 0)}
                          className="w-16 h-10 text-center border-t border-b border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg font-medium"
                        />
                        <button 
                          onClick={() => handleFrequencyChange('post', Math.min(7, formData.frequency.post + 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-r-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  

                </div>
              </div>
              
              <GenerationLimitsStatus 
                className="mt-4" 
                generationType="ContentPlan" 
                status={generationStatus}
                loading={generationLoading}
                error={generationError}
              />
              
              {/* Сообщение о валидации */}
              {showValidationMessage && (
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {validationMessage}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-between mt-6">
                <Button
                  onClick={() => setCurrentStep(1)}
                  variant="secondary"
                  className="flex items-center"
                >
                  Назад
                </Button>
                <Button
                  onClick={handleGenerateContentPlan}
                  disabled={!generationStatus?.canGenerate}
                  variant="primary"
                  className="flex items-center"
                >
                  Создать контент-план
                </Button>
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="max-w-full mx-auto px-0 py-8 sm:px-4">
            <div className="flex justify-end mb-6">
              <Button
                onClick={openSaveDialog}
                variant="primary"
                className="flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Сохранить в базе знаний
              </Button>
            </div>
            <ContentPlanTable planData={contentPlanData} contentPlanId={id || ''} />
          </div>
        );
        
      default:
        return null;
    }
  };

  // Очищаем таймеры при размонтировании компонента
  useEffect(() => {
    return () => {
      if (socialHandleErrorTimeout) {
        clearTimeout(socialHandleErrorTimeout);
      }
      if (competitorHandleErrorTimeout) {
        clearTimeout(competitorHandleErrorTimeout);
      }
    };
  }, [socialHandleErrorTimeout, competitorHandleErrorTimeout]);

  // Эффекты для автоматического фокуса на полях ввода
  useEffect(() => {
    if (isEditingSocialHandle && socialHandleInputRef.current) {
      socialHandleInputRef.current.focus();
    }
  }, [isEditingSocialHandle]);

  useEffect(() => {
    if (isEditingCompetitorHandle && competitorHandleInputRef.current) {
      competitorHandleInputRef.current.focus();
    }
  }, [isEditingCompetitorHandle]);

  // Автоматическое изменение размера textarea при изменении данных формы
  useEffect(() => {
    autoResizeAllTextareas();
  }, [formData.topic, formData.description, formData.services, formData.aboutAuthor, formData.contentPreferences, autoResizeAllTextareas]);

  if (isLoading) {
    return <LoadingAnimation withNavigation size="large" text="Создание контент-плана..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-800 dark:to-gray-900 transition-colors duration-200 mobile-content-padding overflow-x-hidden">
      <div className="hidden md:block">
        <NavigationMenu />
      </div>
      <div className="w-full max-w-7xl mx-auto px-4 py-8 lg:py-12">
        {currentStep < 3 && (
          <>
            <PageHeader
              title="Контент-план"
              subtitle={`Создайте эффективный контент-план для продвижения (Шаг ${currentStep} из 2)`}
            />
            
            <div className="w-full max-w-4xl mx-auto">
              {renderStepContent()}
            </div>
          </>
        )}
        
        {currentStep === 3 && renderStepContent()}
        </div>

        <SaveContentDialog
          isOpen={showSaveDialog}
          onClose={() => setShowSaveDialog(false)}
          onSave={(targetFolderId, fileName) => handleSave(targetFolderId, fileName)}
          items={articles}
          defaultFileName={formData.topic || 'Контент-план'}
          contentType="контент-план"
          title="Сохранить контент-план"
        />

        {/* Интервью для заполнения полей */}
        <InterviewChat
          isOpen={showInterview}
          onClose={() => setShowInterview(false)}
          onInterviewCompleted={handleInterviewCompleted}
        />

        {/* Модальное окно исследования инфоповодов */}
        <InfoResearchChat
          isOpen={showInfoResearch}
          onClose={() => setShowInfoResearch(false)}
        />
      </div>
    );
}

export default ContentPlan;
